import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
  KanbanCard,
  KanbanColumn,
  KanbanConfig,
  CardMovedEvent,
  BatchSelectionChangeEvent,
  BatchActionEvent,
  SelectAllInColumnEvent,
  SelectionPruneEvent,
} from './models/kanban.model';
import {
  KanbanAction,
  KanbanColumnBadge,
  KanbanColumnButton,
  KanbanCardBadge,
} from './models/kanban-action.model';
import { IKanbanDataProvider } from './interfaces/kanban-data-provider.interface';
import { KanbanBoardSelection } from './kanban-board-selection';
import { ToastService } from '@core/components/toasts/toasts.service';

@Component({
  selector: 'app-kanban-board',
  templateUrl: './kanban-board.component.html',
  styleUrls: ['./kanban-board.component.scss'],
})
export class KanbanBoardComponent implements OnInit, OnChanges {
  @Input() columns: KanbanColumn[] = [];

  @Input() config: KanbanConfig = {
    enableDragDrop: true,
    showCardCount: true,
    columnMinWidth: '280px',
  };

  @Input() searchQuery: string = '';

  @Input() movingCardId: string | number | null = null;

  @Input() provider!: IKanbanDataProvider;

  @Input() enableSelection = false;

  @Input() selectedCardIds: Array<string | number> = [];

  @Input() selectionColumnId: string | number | null = null;

  @Input() processingBatch = false;

  @Output() selectionChange = new EventEmitter<BatchSelectionChangeEvent>();

  @Output() selectAllInColumn = new EventEmitter<SelectAllInColumnEvent>();

  @Output() batchAction = new EventEmitter<BatchActionEvent>();

  @Output() selectionPrune = new EventEmitter<SelectionPruneEvent>();

  @Output() selectionCancel = new EventEmitter<void>();

  @Output() cardMoved = new EventEmitter<CardMovedEvent>();

  @Output() cardClicked = new EventEmitter<KanbanCard>();

  @Output() downloadResume = new EventEmitter<KanbanCard>();

  @Output() batchScreening = new EventEmitter<KanbanColumn>();

  @Output() requestPreRegistration = new EventEmitter<KanbanColumn>();

  readonly selection = new KanbanBoardSelection(this);

  public filteredColumns: KanbanColumn[] = [];

  columnIds: string[] = [];

  constructor(private _toastrService: ToastService) {}

  ngOnInit(): void {
    this.initializeColumnIds();
    this.buildFilteredColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.initializeColumnIds();
    }
    this.buildFilteredColumns();
    if (changes['searchQuery'] && !changes['searchQuery'].firstChange) {
      this.selection.emitVisibleSelection();
    }
  }

  private initializeColumnIds(): void {
    this.columnIds = this.columns.map(col => `column-${col.id}`);
  }

  private buildFilteredColumns(): void {
    this.filteredColumns = this.columns.map(column => ({
      ...column,
      badges: this.getColumnBadges(column),
      cards: column.cards.map(card => ({
        ...card,
        actions: this.getCardActions(card, column),
        badges: this.getCardBadges(card),
      })),
    }));
  }

  private syncColumns(previousColumnId: string | number, currentColumnId: string | number, previousIndex: number, currentIndex: number): void {
    const sourceColumn = this.columns.find(col => `${col.id}` === `${previousColumnId}`);
    const targetColumn = this.columns.find(col => `${col.id}` === `${currentColumnId}`);

    if (!sourceColumn || !targetColumn) {
      return;
    }

    if (sourceColumn === targetColumn) {
      moveItemInArray(sourceColumn.cards, previousIndex, currentIndex);
    } else {
      const cardToMove = sourceColumn.cards[previousIndex];

      if (!cardToMove) {
        return;
      }

      sourceColumn.cards.splice(previousIndex, 1);
      targetColumn.cards.splice(currentIndex, 0, cardToMove as KanbanCard);
    }

    this.buildFilteredColumns();
  }

  private refreshMovedCardActions(columnId: string | number, cardIndex: number): void {
    const targetColumn = this.filteredColumns.find(col => `${col.id}` === `${columnId}`);
    if (!targetColumn) {
      return;
    }

    const card = targetColumn.cards[cardIndex];
    if (!card) {
      return;
    }

    card.actions = this.getCardActions(card, targetColumn);
    card.badges = this.getCardBadges(card);
  }

  getColumnId(column: KanbanColumn): string {
    return `column-${column.id}`;
  }

  isCardDragDisabled(card: KanbanCard): boolean {
    return (
      card?.dragDisabled === true ||
      card?.isDragDisabled === true ||
      card?.metadata?.dragDisabled === true ||
      card?.metadata?.isDragDisabled === true
    );
  }

  onCardDrop(event: CdkDragDrop<KanbanCard[]>, column: KanbanColumn): void {
    if (!this.config.enableDragDrop) {
      return;
    }

    if (event.previousContainer !== event.container && this.isColumnLimitReached(column)) {
      this._toastrService.showWarning(this.getLimitReachedMessage(column));
      return;
    }

    const previousColumnId = event.previousContainer.id.replace('column-', '');
    const currentColumnId = column.id;
    const movedCard = event.previousContainer.data[event.previousIndex] as KanbanCard;
    if (!movedCard || this.isCardDragDisabled(movedCard)) {
      return;
    }

    const prevIndex = this.columns.findIndex(c => `${c.id}` === `${previousColumnId}`);
    const currIndex = this.columns.findIndex(c => `${c.id}` === `${currentColumnId}`);

    const showMoveDeniedWarning = () => {
      const message = this.resolveMoveDenyMessage(
        movedCard,
        previousColumnId,
        currentColumnId,
        prevIndex,
        currIndex as number
      );
      this._toastrService.showWarning(message);
    };

    const performMove = () => {
      if (event.previousContainer === event.container) {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      } else {
        transferArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex
        );
      }

      const moved = event.container.data[event.currentIndex];
      this.refreshMovedCardActions(currentColumnId, event.currentIndex);
      this.syncColumns(previousColumnId, currentColumnId, event.previousIndex, event.currentIndex);
      this.cardMoved.emit({
        card: moved,
        previousColumnId: previousColumnId,
        currentColumnId: currentColumnId,
        previousIndex: event.previousIndex,
        currentIndex: event.currentIndex,
      });
    };

    if (this.provider && this.provider.canMoveCard) {
      const result = this.provider.canMoveCard(movedCard, previousColumnId, currentColumnId, prevIndex, currIndex as number);
      if (result && typeof (result as any).subscribe === 'function') {
        (result as any).subscribe(
          (allowed: boolean) => {
            if (allowed) {
              performMove();
            } else {
              showMoveDeniedWarning();
            }
          },
          () => {
            showMoveDeniedWarning();
          }
        );
      } else if (result === true) {
        performMove();
      } else if (result === false) {
        showMoveDeniedWarning();
      }
    } else {
      performMove();
    }
  }

  onCardClick(card: KanbanCard): void {
    this.cardClicked.emit(card);
  }

  trackByCardId(_index: number, card: KanbanCard): string | number {
    return card.id;
  }

  getCardCount(column: KanbanColumn): number {
    const originalColumn = this.columns?.find(col => `${col.id}` === `${column.id}`);
    return originalColumn?.cards?.length ?? column.cards?.length ?? 0;
  }

  getColumnCountLabel(column: KanbanColumn): string {
    const total = this.getCardCount(column);

    if (this.selection.isSelectionActiveForColumn(column)) {
      return `${this.selectedCardIds.length} de ${total}`;
    }

    if (!this.hasSearchQuery() || total === 0) {
      return String(total);
    }

    const visible = column.cards?.length ?? 0;
    return `${visible}/${total}`;
  }

  getColumnCountTooltip(column: KanbanColumn): string {
    const total = this.getCardCount(column);

    if (!this.hasSearchQuery() || total === 0) {
      return `Total: ${total}`;
    }

    const visible = column.cards?.length ?? 0;
    return `Exibindo ${visible} de ${total}`;
  }

  isColumnLimitReached(column: KanbanColumn): boolean {
    if (!column.limit) {
      return false;
    }
    return this.getCardCount(column) >= column.limit;
  }

  getTotalCardsCount(): number {
    return this.columns.reduce((total, column) => total + (column.cards?.length || 0), 0);
  }

  hasSearchQuery(): boolean {
    return this.searchQuery && this.searchQuery.trim().length > 0;
  }

  getColumnBadges(column: KanbanColumn): KanbanColumnBadge[] {
    if (!this.provider || !this.provider.getColumnBadges) {
      return [];
    }
    return this.provider.getColumnBadges(column);
  }

  getColumnButtons(column: KanbanColumn): KanbanColumnButton[] {
    if (!this.provider || !this.provider.getColumnButtons) {
      return [];
    }
    return this.provider.getColumnButtons(column);
  }

  getCardActions(card: KanbanCard, column: KanbanColumn): KanbanAction[] {
    if (!this.provider || !this.provider.getCardActions) {
      return [];
    }
    return this.provider.getCardActions(card, column);
  }

  getCardBadges(card: KanbanCard): KanbanCardBadge[] {
    if (!this.provider || !this.provider.getCardBadges) {
      return [];
    }
    return this.provider.getCardBadges(card);
  }

  hasColumnBadges(column: KanbanColumn): boolean {
    return this.getColumnBadges(column).length > 0;
  }

  hasColumnButtons(column: KanbanColumn): boolean {
    return this.getColumnButtons(column).length > 0;
  }

  hasCardActions(card: KanbanCard, column: KanbanColumn): boolean {
    return this.getCardActions(card, column).length > 0;
  }

  onColumnButtonClick(button: KanbanColumnButton, column: KanbanColumn, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (button.handler) {
      button.handler(column);
    } else if (this.provider && this.provider.onColumnButtonClick) {
      this.provider.onColumnButtonClick(button.id, column);
    }
  }

  onCardActionClick(action: KanbanAction, card: KanbanCard, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (action.handler) {
      action.handler({ card, action });
    } else if (this.provider && this.provider.onCardActionClick) {
      this.provider.onCardActionClick(action.id, card);
    }
  }

  onCardBadgeClick(badge: KanbanCardBadge, card: KanbanCard, event: MouseEvent): void {
    event.stopPropagation();
    if (badge.handler) {
      badge.handler(card, event);
    } else if (this.provider && this.provider.onCardBadgeClick) {
      this.provider.onCardBadgeClick(badge.id, card, event);
    }
  }

  isActionDivider(action: KanbanAction): boolean {
    return action.divider === true;
  }

  isActionVisible(action: KanbanAction, context: any): boolean {
    if (action.divider) return true;
    if (!action.visible) return true;
    return action.visible(context);
  }

  isActionDisabled(action: KanbanAction, context: any): boolean {
    if (!action.disabled) return false;
    return action.disabled(context);
  }

  isBadgeVisible(badge: KanbanColumnBadge | KanbanCardBadge, context: any): boolean {
    if (!badge.visible) return true;
    return badge.visible(context);
  }

  hasBadgePopover(badge: KanbanColumnBadge): boolean {
    return badge.popoverData !== undefined;
  }

  getBadgePopoverData(badge: KanbanColumnBadge): any {
    return badge.popoverData;
  }

  hasActions(column: KanbanColumn): boolean {
    return column.actions && Array.isArray(column.actions) && column.actions.length > 0;
  }

  getColumnActions(column: KanbanColumn): any[] {
    if (!column.actions || !Array.isArray(column.actions)) {
      return [];
    }
    return column.actions;
  }

  onColumnAction(column: KanbanColumn, action: any, event: MouseEvent): void {
    event.stopPropagation();

    const actionValue = action.action || action;

    if (actionValue === 'BatchScreening') {
      this.batchScreening.emit(column);
      return;
    }

    if (this.provider && this.provider.onColumnButtonClick) {
      this.provider.onColumnButtonClick(String(actionValue), column);
    }
    if (actionValue === 'RequestPreRegistration') {
      this.requestPreRegistration.emit(column);
    }
    if (actionValue === 'RequestPreRegistration') {
      this.requestPreRegistration.emit(column);
    }
  }

  getActionLabel(action: any): string {
    if (action && typeof action === 'object' && action.label) {
      return String(action.label);
    }

    const actionValue = action.action || action;
    switch (actionValue) {
      case 'BatchScreening':
        return 'Triagem em lote';
      case 'RequestPreRegistration':
        return 'Solicitar pré-cadastro';
      case 'Scheduling':
        return 'Agendamento';
      case 'Approve':
        return 'Aprovar';
      case 'Reject':
        return 'Rejeitar';
      case 'Interview':
        return 'Entrevista';
      default:
        return actionValue;
    }
  }

  getInterviewStats(column: KanbanColumn): { total: number; confirmed: number; rejected: number } {
    const total = column.cards?.length || 0;

    const confirmed =
      column.cards?.filter(card => card.status === 'confirmed' || card.status === 'scheduled')
        .length || 0;

    const rejected =
      column.cards?.filter(card => card.status === 'rejected' || card.status === 'cancelled')
        .length || 0;

    return { total, confirmed, rejected };
  }

  private resolveMoveDenyMessage(
    card: KanbanCard,
    previousColumnId: string | number,
    currentColumnId: string | number,
    previousIndex: number,
    currentIndex: number
  ): string {
    const specific = this.provider?.getMoveDenyMessage?.(
      card,
      previousColumnId,
      currentColumnId,
      previousIndex,
      currentIndex
    );
    return specific || this.getDenyMessage(previousIndex, currentIndex);
  }

  private getDenyMessage(previousIndex: number, currentIndex: number): string {
    if (currentIndex === previousIndex) return 'Movimento não permitido para este card';
    if (currentIndex < previousIndex) return 'Não é permitido retroceder';
    if (currentIndex > previousIndex + 1) return 'Não é permitido pular etapas';
    return 'Movimento não permitido';
  }

  private getLimitReachedMessage(column: KanbanColumn): string {
    const limit = column.limit;
    const title = column.title || 'etapa';

    if (!limit) {
      return 'Limite atingido';
    }

    return `Limite de ${limit} item(ns) atingido na etapa "${title}"`;
  }
}
